import { i18n } from '@/i18n';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { procurementApi, type RfqSummary } from '@/api/procurement.api';
import type { SelectOption } from '@/components/ui/Select';
import { useDebounce } from '@/hooks/useDebounce';
import { showApiError } from '@/utils/apiError';

function rfqOption(row: RfqSummary): SelectOption {
  return { value: String(row.id), label: row.rfqNumber };
}

function mergeSelectedOption(options: SelectOption[], selected?: SelectOption | null): SelectOption[] {
  if (!selected) return options;
  if (options.some((opt) => opt.value === selected.value)) return options;
  return [selected, ...options];
}

type Params = { value?: string; pageSize?: number };

export function useRfqSelectSearch({ value = '', pageSize = 30 }: Params = {}) {
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 300);
  const [rows, setRows] = useState<RfqSummary[]>([]);
  const [selected, setSelected] = useState<RfqSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);

  const load = useCallback(
    async (search: string) => {
      setSearching(true);
      try {
        const res = await procurementApi.listRfqs({
          search: search || undefined,
          page: 0,
          size: pageSize,
        });
        setRows(res.content);
      } catch (err) {
        showApiError(err, i18n.t('errors.failedToLoadRfqs', { ns: 'common' }));
      } finally {
        setSearching(false);
        setLoading(false);
      }
    },
    [pageSize],
  );

  useEffect(() => {
    void load(debouncedSearch);
  }, [debouncedSearch, load]);

  useEffect(() => {
    if (!value) {
      setSelected(null);
      return;
    }
    const id = Number(value);
    if (Number.isNaN(id)) return;
    const fromList = rows.find((r) => r.id === id);
    if (fromList) {
      setSelected(fromList);
    }
  }, [value, rows]);

  const options = useMemo(() => {
    const base = rows.map(rfqOption);
    const sel = selected ? rfqOption(selected) : null;
    return mergeSelectedOption(base, sel);
  }, [rows, selected]);

  const byId = useMemo(() => {
    const map = new Map<number, RfqSummary>();
    for (const r of rows) map.set(r.id, r);
    if (selected) map.set(selected.id, selected);
    return map;
  }, [rows, selected]);

  const getRfq = useCallback(
    (id: number | string | null | undefined) => {
      if (id == null || id === '') return undefined;
      return byId.get(Number(id));
    },
    [byId],
  );

  return { options, loading, searching, onSearchQuery: setSearchQuery, getRfq };
}
